import { forwardRef, useCallback, useEffect, useState } from 'react';
import { useForm, useWatch, useFormState, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';

import ErrorMessage from 'components/ErrorMessage';
import SelectInput from 'components/SelectInput';
import CountrySelect from 'components/CountrySelect';
import Button from 'components/Button';
import FormInput from 'components/FormInput';
import TextArea from 'components/TextArea';
import UploadButton from 'components/UploadButton/UploadButton.admin';
import {
  showErrorMessage,
  showSuccessMessage,
} from 'redux/slices/notification';

import { evidenceForm as TEXT } from 'locales/en/dispute';
import schema, {
  FIELD_NAMES,
  defaultValues,
  PRODUCT_OPTIONS,
  EVIDENCE_OPTIONS,
} from './EvidenceForm.stripe.schema';

import 'utils/vapor';
import { useDispatch } from 'react-redux';
import {
  disputeByIdSelector,
  getDisputesById,
  getStripeDispute,
} from 'redux/slices/dispute';
import { useSelector } from 'react-redux';
import { SUBMIT_STRIPE_DISPUTE } from 'services/api';
import { UploadedDocumentList } from './UploadedDocumentsList';

const LABEL_CLASSNAME =
  'text-xs text-javelin-gray-light pb-1 lg:pb-0 lg:w-4/12';

const EvidenceFormStripe = ({ dispute }, ref) => {
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    control,
    setValue,
    trigger,
    getValues,
    setError,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  useEffect(() => {
    dispatch(getStripeDispute(dispute.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { stripe_dispute } = useSelector((state) =>
    disputeByIdSelector(state, dispute.id)
  );

  const updateUploadedDocuments = useCallback((stripeEvidence) => {
    const docTypes = EVIDENCE_OPTIONS.map((docType) => docType.value);
    const documents = docTypes.reduce((fileCollection, docType) => {
      if (stripeEvidence[docType]) {
        fileCollection.push({ type: docType, id: stripeEvidence[docType] });
      }
      return fileCollection;
    }, []);

    setUploadedFiles(documents);
  }, []);

  useEffect(() => {
    setValue(FIELD_NAMES.name, stripe_dispute?.evidence?.customer_name ?? '');
    setValue(
      FIELD_NAMES.email,
      stripe_dispute?.evidence?.customer_email_address ?? ''
    );
    setValue(
      FIELD_NAMES.address_1,
      stripe_dispute?.evidence?.billing_address ?? ''
    );
    setValue(
      FIELD_NAMES.description,
      stripe_dispute?.evidence?.product_description ?? ''
    );
    if (stripe_dispute?.evidence) {
      updateUploadedDocuments(stripe_dispute.evidence);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripe_dispute]);

  const { errors, isSubmitting } = useFormState({
    control,
  });

  const [file] = useWatch({
    control,
    name: [FIELD_NAMES.file],
  });

  const onSubmit = async (data) => {
    const country = data['country']?.name;
    const billing_address = `${data.address_1} ${data.address_2} ${data.city} ${data.postcode} ${country}`;

    const formData = {
      customer_name: data.name,
      customer_email_address: data.email,
      billing_address: billing_address,
      product_description: data.description,
      uncategorized_text: stripe_dispute?.evidence?.uncategorized_text,
      files: uploadedFiles,
      submit: data.submit,
    };

    SUBMIT_STRIPE_DISPUTE(dispute.id, formData).then(
      (res) => {
        dispatch(getDisputesById(dispute.id));
        dispatch(getStripeDispute(dispute.id));
        dispatch(showSuccessMessage(TEXT.submitEvidenceSuccess));
        setIsLoading(false);
      },
      (err) => {
        dispatch(
          showErrorMessage(
            data.submit ? TEXT.submitEvidenceError : TEXT.saveEvidenceError
          )
        );
        setIsLoading(false);
      }
    );
  };

  const handleDeleteFile = () => {
    setValue(FIELD_NAMES.file, null);
  };

  const handleSelectFile = async (_file) => {
    const docType = getValues(FIELD_NAMES.supporting_evidence).value;

    if (!docType) {
      setValue('file', null);
      setError(FIELD_NAMES.supporting_evidence, {
        type: 'validate',
        message: TEXT.errors.supporting_evidence,
      });
      return;
    }

    setValue('file', _file, { shouldValidate: true });
    await trigger('file');

    if (errors.file?.message) return;

    let publishableApiKey =
      dispute.transaction?.integration?.data?.stripe_publishable_api_key;
    let directConnectCharge = dispute.transaction?.is_stripe_connect_payment;
    let accountId = dispute.transaction?.company?.data?.stripe_account_id;

    setUploadProgress(0);

    let formData = new FormData();
    formData.append('file', _file);
    formData.append('purpose', 'dispute_evidence');
    formData.append('key', publishableApiKey);

    let options = {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        setUploadProgress(
          Math.floor((progressEvent.loaded * 100) / progressEvent.total)
        );
      },
    };

    if (directConnectCharge) {
      options['headers']['Stripe-Account'] = accountId;
    }

    axios
      .post('https://files.stripe.com/v1/files', formData, options)
      .then((response) => {
        setUploadedFiles([
          {
            id: response.data.id,
            name: response.data.filename,
            type: docType,
          },
          ...uploadedFiles,
        ]);

        setValue('file', null);
        setValue('supporting_evidence', '');
      })
      .catch((error) => {
        if (error.response.data.error && error.response.data.error.message) {
          dispatch(showErrorMessage(error.response.data.error.message));

          setError(FIELD_NAMES.supporting_evidence, {
            type: 'validate',
            message: error.response.data.error.message,
          });
        } else {
          dispatch(showErrorMessage('Unexpected error, please try again'));

          setError(FIELD_NAMES.supporting_evidence, {
            type: 'validate',
            message: 'Unexpected error, please try again',
          });
        }
      });
  };

  const handleDeleteDocument = (documentId) => {
    const documents = uploadedFiles.filter(
      (document) => document.id !== documentId
    );

    setUploadedFiles(documents);
  };

  return (
    <>
      <h2
        className="mb-5 text-base font-bold text-javelin-gray-dark lg:text-xl"
        ref={ref}
      >
        {TEXT.submit}
      </h2>
      <form
        noValidate
        onSubmit={(e) => {
          setValue('submit', true);
          handleSubmit(onSubmit)(e);
        }}
        className="mb-8 w-full"
      >
        <div className="card p-0">
          <div className="flex w-full flex-col p-12 lg:w-11/12 xl:w-8/12">
            <FormInput
              label={TEXT.name}
              name={FIELD_NAMES.name}
              register={register}
              error={errors[FIELD_NAMES.name]?.message}
              labelClassName={LABEL_CLASSNAME}
            />
            <FormInput
              label={TEXT.email}
              name={FIELD_NAMES.email}
              register={register}
              error={errors[FIELD_NAMES.email]?.message}
              labelClassName={LABEL_CLASSNAME}
            />
            <FormInput
              label={TEXT.billing_address}
              placeholder={TEXT.address_1}
              name={FIELD_NAMES.address_1}
              register={register}
              required
              error={errors[FIELD_NAMES.address_1]?.message}
              labelClassName={LABEL_CLASSNAME}
            />
            <FormInput
              placeholder={TEXT.address_2}
              name={FIELD_NAMES.address_2}
              register={register}
              className="-mt-2"
              labelClassName={LABEL_CLASSNAME}
            />
            <FormInput
              placeholder={TEXT.postcode}
              name={FIELD_NAMES.postcode}
              register={register}
              className="-mt-2"
              labelClassName={LABEL_CLASSNAME}
            />
            <FormInput
              placeholder={TEXT.city}
              name={FIELD_NAMES.city}
              register={register}
              error={errors[FIELD_NAMES.city]?.message}
              className="-mt-2"
              labelClassName={LABEL_CLASSNAME}
            />
            <CountrySelect
              name={FIELD_NAMES.country}
              control={control}
              required
              label={null}
              inputBelowLabel={false}
              error={errors[FIELD_NAMES.country]?.name?.message}
              inputWrapperClassName="-mt-2 flex-1"
              labelClassName={LABEL_CLASSNAME}
            />
          </div>

          <hr className="horizontal-line m-0" />

          <div className="flex w-full flex-col p-12 lg:w-11/12 xl:w-8/12">
            <Controller
              control={control}
              name={FIELD_NAMES.product}
              render={({ field: { ..._field } }) => (
                <div className="mb-5 flex flex-1 flex-row">
                  <span className={LABEL_CLASSNAME}>
                    {TEXT.product_details}
                  </span>
                  <div className="input-form-wrapper grid grid-flow-row gap-y-5">
                    {PRODUCT_OPTIONS.map((item) => (
                      <label
                        htmlFor={item.value}
                        name={FIELD_NAMES.product}
                        key={item.value}
                      >
                        <input
                          {..._field}
                          type="radio"
                          className="mr-2 h-5 w-5 border border-gray-300 p-1 text-javelin-blue-dark focus:border-javelin-blue-dark focus:ring-transparent"
                          id={item.value}
                          value={item.value}
                          name={FIELD_NAMES.product}
                        />
                        <span className="text-sm text-javelin-gray-dark">
                          {item.label}
                        </span>
                      </label>
                    ))}
                    {errors[FIELD_NAMES.product]?.message && (
                      <ErrorMessage
                        message={errors[FIELD_NAMES.product]?.message}
                      />
                    )}
                  </div>
                </div>
              )}
            />
            <TextArea
              label={TEXT.description}
              name={FIELD_NAMES.description}
              register={register}
              labelClassName={LABEL_CLASSNAME}
            />
          </div>

          <hr className="horizontal-line m-0" />

          <div className="w-full bg-javelin-background">
            <div className="flex w-full flex-col p-12 ">
              <Controller
                control={control}
                name={FIELD_NAMES.supporting_evidence}
                render={({ field: { ref, value, ..._field } }) => (
                  <SelectInput
                    {..._field}
                    value={value}
                    testId={FIELD_NAMES.supporting_evidence}
                    name={FIELD_NAMES.supporting_evidence}
                    label={TEXT.supporting_evidence}
                    options={EVIDENCE_OPTIONS.filter(
                      (option) =>
                        uploadedFiles.findIndex(
                          (el) => el.type === option.value
                        ) === -1
                    )}
                    inputBelowLabel={false}
                    placeholder={TEXT.industry_placeholder}
                    labelClassName={LABEL_CLASSNAME}
                    error={errors[FIELD_NAMES.supporting_evidence]?.message}
                  />
                )}
              />
              <Controller
                control={control}
                name={FIELD_NAMES.file}
                render={() => (
                  <UploadButton
                    label={TEXT.uploadLabel}
                    hideDeleteButton
                    name={FIELD_NAMES.file}
                    docType={getValues(FIELD_NAMES.supporting_evidence).label}
                    onSelectFile={handleSelectFile}
                    onDeleteFile={handleDeleteFile}
                    error={errors[FIELD_NAMES.file]?.message}
                    file={file}
                    progress={uploadProgress}
                  />
                )}
              />

              <UploadedDocumentList
                disputeId={dispute.id}
                documentList={uploadedFiles}
                onDeleteClick={handleDeleteDocument}
              />

              <TextArea
                label={TEXT.additional_info}
                name={FIELD_NAMES.additional_info}
                register={register}
                labelClassName={LABEL_CLASSNAME}
              />
            </div>
          </div>

          <hr className="horizontal-line m-0" />

          <div className="flex items-center bg-white p-12">
            <p className="mr-4 text-xs text-javelin-gray-light">
              {TEXT.submitInfo}
            </p>
            <Button
              className="btn-secondary ml-5 block h-12 w-56 px-0 lg:w-48"
              type="button"
              title={TEXT.save}
              onClick={(e) => {
                setValue('submit', false);
                handleSubmit(onSubmit)(e);
              }}
              showSpinner={isSubmitting || isLoading}
              isDisabled={isSubmitting || isLoading}
            />
            <Button
              className="btn-primary ml-5 block h-12 w-56 px-0 lg:w-48"
              type="submit"
              title={TEXT.submit}
              showSpinner={isSubmitting || isLoading}
              isDisabled={isSubmitting || isLoading}
            />
          </div>
        </div>
      </form>
    </>
  );
};

export default forwardRef(EvidenceFormStripe);
